import { normalizeSlotLabel } from "@/lib/manufacturing/slotLabel";
import type { ComposeNode, ComposePlan } from "./types";

export type CommitComponentLine = {
  nodeId: string;
  /** Product BOM line slot_label from the planned slot key/label. */
  slot_label: string;
};

export type CommitCreateItem = {
  nodeId: string;
  code: string;
  name: string;
  kind: ComposeNode["kind"];
  tags: string[];
  role: ComposeNode["role"];
  needs_review: true;
  /** Component lines to wire after stub creates (node id + slot label). */
  componentLines: CommitComponentLine[];
};

export type CommitPlanOrder = {
  /** Theoretical assemblies/packages to create first. */
  stubs: CommitCreateItem[];
  /** Products to create (or link) with component lines. */
  products: CommitCreateItem[];
  /** Nodes already matched to existing recipes — used when wiring lines. */
  matchedByNodeId: Record<string, string>;
};

export type ComposeCommitResult = {
  createdStubs: string[];
  createdProducts: string[];
  linkedProducts: string[];
  failed: Array<{ code: string; error: string }>;
  recipeIdByNodeId: Record<string, string>;
  committedProductNodeIds: string[];
};

function nodesById(plan: ComposePlan): Map<string, ComposeNode> {
  return new Map(plan.nodes.map((n) => [n.id, n]));
}

/** True when every product slot is matched or theoretical (not unmatched). */
export function isProductBomReady(product: ComposeNode, byId: Map<string, ComposeNode>): boolean {
  if (product.role !== "product") return false;
  // Already committed to the catalog in a prior partial commit.
  if (product.match.status === "matched") return false;
  if (product.slots.length === 0) return false;
  for (const slot of product.slots) {
    if (!slot.childNodeId) return false;
    const child = byId.get(slot.childNodeId);
    if (!child) return false;
    if (child.match.status === "unmatched") return false;
  }
  return true;
}

export function listReadyProducts(plan: ComposePlan): ComposeNode[] {
  const byId = nodesById(plan);
  return plan.nodes.filter((n) => n.role === "product" && isProductBomReady(n, byId));
}

export function listCommittedProducts(plan: ComposePlan): ComposeNode[] {
  return plan.nodes.filter((n) => n.role === "product" && n.match.status === "matched");
}

export function listPendingProducts(plan: ComposePlan): ComposeNode[] {
  const byId = nodesById(plan);
  return plan.nodes.filter(
    (n) =>
      n.role === "product" &&
      n.match.status !== "matched" &&
      !isProductBomReady(n, byId),
  );
}

/** Products not yet committed to the catalog (ready + pending). */
export function listUnfinishedProducts(plan: ComposePlan): ComposeNode[] {
  return plan.nodes.filter((n) => n.role === "product" && n.match.status !== "matched");
}

/**
 * Components still referenced by unfinished products.
 * Keeps shared matched assemblies visible for remaining SKUs; drops work
 * only needed by already-committed products.
 */
export function listActiveComponents(plan: ComposePlan): ComposeNode[] {
  const unfinished = listUnfinishedProducts(plan);
  const needed = new Set<string>();
  for (const p of unfinished) {
    for (const slot of p.slots) {
      if (slot.childNodeId) needed.add(slot.childNodeId);
    }
  }
  return plan.nodes.filter((n) => n.role === "component" && needed.has(n.id));
}

/**
 * Build topological commit order for a subset of products (or all ready if omitted).
 * Only includes theoretical stubs referenced by those products.
 */
export function buildCommitPlanOrder(
  plan: ComposePlan,
  opts?: { productNodeIds?: readonly string[] },
): CommitPlanOrder {
  const matchedByNodeId: Record<string, string> = {};
  const stubs: CommitCreateItem[] = [];
  const products: CommitCreateItem[] = [];
  const byId = nodesById(plan);

  for (const n of plan.nodes) {
    if (n.match.status === "matched") {
      matchedByNodeId[n.id] = n.match.recipe_id;
    }
  }

  const ready = listReadyProducts(plan);
  const selectedIds = opts?.productNodeIds?.length
    ? new Set(opts.productNodeIds)
    : new Set(ready.map((p) => p.id));

  const selectedProducts = plan.nodes.filter(
    (n) => n.role === "product" && selectedIds.has(n.id) && isProductBomReady(n, byId),
  );

  const stubNodeIds = new Set<string>();
  for (const p of selectedProducts) {
    for (const slot of p.slots) {
      if (!slot.childNodeId) continue;
      const child = byId.get(slot.childNodeId);
      if (child?.match.status === "theoretical") stubNodeIds.add(child.id);
    }
  }

  for (const n of plan.nodes) {
    if (n.role !== "component") continue;
    if (!stubNodeIds.has(n.id)) continue;
    stubs.push({
      nodeId: n.id,
      code: n.code,
      name: n.name,
      kind: n.kind,
      tags: n.tags,
      role: n.role,
      needs_review: true,
      componentLines: [],
    });
  }

  for (const n of selectedProducts) {
    products.push({
      nodeId: n.id,
      code: n.code,
      name: n.name,
      kind: n.kind,
      tags: n.tags,
      role: n.role,
      needs_review: true,
      componentLines: n.slots
        .filter((s): s is typeof s & { childNodeId: string } => Boolean(s.childNodeId))
        .map((s) => ({
          nodeId: s.childNodeId,
          slot_label: normalizeSlotLabel(s.slotKey || s.label),
        })),
    });
  }

  return { stubs, products, matchedByNodeId };
}

/**
 * After a partial commit: mark created stubs/products as matched, append confirmed links.
 * Leaves unfinished nodes in the plan for resume.
 */
export function applyPartialCommitToPlan(
  plan: ComposePlan,
  result: ComposeCommitResult,
  committedProductNodeIds: readonly string[],
): ComposePlan {
  const committedSet = new Set(committedProductNodeIds);
  const byId = nodesById(plan);
  const confirmedLinks = [...(plan.confirmedLinks ?? [])];
  const matchBoosts = [...plan.matchBoosts];

  const nextNodes = plan.nodes.map((n) => {
    const recipeId = result.recipeIdByNodeId[n.id];
    if (!recipeId) return n;

    if (n.role === "component" && n.match.status === "theoretical") {
      const code = n.code;
      if (!matchBoosts.includes(code)) matchBoosts.push(code, n.name);
      if (n.slotKey) {
        confirmedLinks.push({
          slotKey: n.slotKey,
          sizeLabel: n.sizeLabel,
          styleKey: n.styleKey,
          variantKey: n.variantKey,
          recipe_id: recipeId,
          code,
        });
      }
      return { ...n, match: { status: "matched" as const, recipe_id: recipeId } };
    }

    if (n.role === "product" && committedSet.has(n.id)) {
      // Record each resolved child link for learning.
      for (const slot of n.slots) {
        if (!slot.childNodeId) continue;
        const child = byId.get(slot.childNodeId);
        const childRecipeId =
          result.recipeIdByNodeId[slot.childNodeId] ??
          (child?.match.status === "matched" ? child.match.recipe_id : null);
        if (!child || !childRecipeId || !child.slotKey) continue;
        const code =
          child.match.status === "matched"
            ? (plan.nodes.find((x) => x.id === child.id)?.code ?? child.code)
            : child.code;
        confirmedLinks.push({
          slotKey: child.slotKey,
          sizeLabel: child.sizeLabel,
          styleKey: child.styleKey,
          variantKey: child.variantKey,
          recipe_id: childRecipeId,
          code: child.code || code,
        });
        if (!matchBoosts.includes(child.code)) matchBoosts.push(child.code, child.name);
      }
      return { ...n, match: { status: "matched" as const, recipe_id: recipeId } };
    }

    return n;
  });

  return {
    ...plan,
    nodes: nextNodes,
    matchBoosts,
    confirmedLinks,
    step: "match",
    updatedAt: new Date().toISOString(),
  };
}

export function componentNodes(plan: ComposePlan): ComposeNode[] {
  return plan.nodes.filter((n) => n.role === "component");
}

export function productNodes(plan: ComposePlan): ComposeNode[] {
  return plan.nodes.filter((n) => n.role === "product");
}

export function planMatchStats(plan: ComposePlan): {
  components: number;
  products: number;
  matched: number;
  theoretical: number;
  unmatched: number;
  readyProducts: number;
  committedProducts: number;
  pendingProducts: number;
} {
  const components = plan.nodes.filter((n) => n.role === "component");
  const products = plan.nodes.filter((n) => n.role === "product");
  let matched = 0;
  let theoretical = 0;
  let unmatched = 0;
  for (const n of components) {
    if (n.match.status === "matched") matched += 1;
    else if (n.match.status === "theoretical") theoretical += 1;
    else unmatched += 1;
  }
  return {
    components: components.length,
    products: products.length,
    matched,
    theoretical,
    unmatched,
    readyProducts: listReadyProducts(plan).length,
    committedProducts: listCommittedProducts(plan).length,
    pendingProducts: listPendingProducts(plan).length,
  };
}
